import type {HTMLAttributes} from 'react'

import {useBoardDataContext} from '../provider/contexts'
import type {BoardColumn, ColumnKey} from '../types'

export type UseBoardColumnResult = {
  /** The column at this key, or `undefined` when the board holds no such column. */
  column: BoardColumn | undefined
  columnProps: HTMLAttributes<HTMLElement>
}

/**
 * A column's frame and its header. A column is not a drop target. A card lands in the
 * cell where a column and a row meet, never in a column as a whole, so this hook
 * registers nothing with the drag layer and will not start doing so.
 *
 * It claims no grouping role either. A column's frame holds only its header; the cells
 * below it belong to the rows, which are separate subtrees. A `group` here would assert
 * a containment that does not exist in the accessibility tree. The column-to-cell
 * relationship is carried by each cell's own accessible name, and the column's name is
 * reachable as a heading.
 */
export function useBoardColumn(columnKey: ColumnKey): UseBoardColumnResult {
  const {columnsByKey} = useBoardDataContext()

  return {
    column: columnsByKey.get(columnKey),
    columnProps: {},
  }
}
